import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Play,
  Square,
  Trash2,
  Download,
  Circle,
  RefreshCw,
  Loader2,
} from "lucide-react";
import apiClient from "@lift-admin/utils/apiClient";
import { logAdminAction } from "@lift-admin/utils/actionLogTrack";
import { useAuthContext } from "@lift-admin/utils/provider";
import { authFirebase } from "@lift-admin/utils/firebase-config";
import { Toastify } from "lift-ui";
import {
  Button,
  Input,
  Badge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  PageHeader,
  DataTable,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getRunLogs, RunLogItem, RunType, RunStatus, EventType, EventStatus } from "../../adverity-logs/api";
import RunLogsTable from "./run-logs-table/run-logs-table.component";
import RunLogsDialog from "./run-logs-dialog/run-logs-dialog.component";
import EventLogsTable from "./event-logs-table/event-logs-table.component";
import EventDetailsDialog from "./event-details-dialog/event-details-dialog.component";
import "./onboarding-logs.styles.scss";

interface LogEntry {
  id: string;
  timestamp: string;
  level: string;
  message: string;
  raw_message?: string;
  logger_name?: string;
  event_type: "start" | "log" | "complete" | "error";
}

interface MonitorConfig {
  batch_size: number;
  timeout_seconds: number;
}

const OnboardingLogs: React.FC = () => {
  const { canManage } = useAuthContext();
  const hasManagerAccess = canManage('adverity_import');

  // Tab state
  const [activeTab, setActiveTab] = useState("live-monitoring");

  // Live streaming state
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [config, setConfig] = useState<MonitorConfig>({
    batch_size: 10,
    timeout_seconds: 60,
  });
  const [summary, setSummary] = useState<any>(null);
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Account reset state
  const [accountId, setAccountId] = useState("");
  const [isResetting, setIsResetting] = useState(false);

  // Run logs dialog state
  const [showRunLogsDialog, setShowRunLogsDialog] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [runLogsDialogMode, setRunLogsDialogMode] = useState<"full" | "errors">("full");

  // Run history (lifted for header filters)
  const [runLogs, setRunLogs] = useState<RunLogItem[]>([]);
  const [runLogsLoading, setRunLogsLoading] = useState(false);
  const [runLogsTotalCount, setRunLogsTotalCount] = useState(0);
  const [runLogsPage, setRunLogsPage] = useState(0);
  const [runLogsRowsPerPage, setRunLogsRowsPerPage] = useState(25);
  const [runTypeFilter, setRunTypeFilter] = useState<RunType | "all">("all");
  const [statusFilter, setStatusFilter] = useState<RunStatus | "all">("all");

  const fetchRunLogs = useCallback(async () => {
    setRunLogsLoading(true);
    try {
      const skip = runLogsPage * runLogsRowsPerPage;
      const limit = runLogsRowsPerPage;
      const { items, total_count } = await getRunLogs(
        skip,
        limit,
        runTypeFilter === "all" ? undefined : runTypeFilter,
        statusFilter === "all" ? undefined : statusFilter
      );
      setRunLogs(items);
      setRunLogsTotalCount(total_count);
    } catch (err) {
      console.error("Error fetching run logs", err);
    } finally {
      setRunLogsLoading(false);
    }
  }, [runLogsPage, runLogsRowsPerPage, runTypeFilter, statusFilter]);

  useEffect(() => {
    if (activeTab === "run-history") {
      fetchRunLogs();
    }
  }, [activeTab, fetchRunLogs]);

  // Event logs filters (lifted for header)
  const [eventTypeFilter, setEventTypeFilter] = useState<EventType | "all">("all");
  const [eventStatusFilter, setEventStatusFilter] = useState<EventStatus | "all">("all");
  const [datastreamIdFilter, setDatastreamIdFilter] = useState("");
  const [eventLogsLoading, setEventLogsLoading] = useState(false);
  const [eventLogsTotalCount, setEventLogsTotalCount] = useState(0);
  const eventLogsRefreshRef = useRef<(() => void) | null>(null);

  // Event logs dialog state
  const [showEventDetailsDialog, setShowEventDetailsDialog] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

  const handleShowRunLogs = (runId: string) => {
    setSelectedRunId(runId);
    setRunLogsDialogMode("full");
    setShowRunLogsDialog(true);
  };

  const handleViewEventDetails = (eventId: number) => {
    setSelectedEventId(eventId);
    setShowEventDetailsDialog(true);
  };

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const startMonitoring = async () => {
    setIsConnecting(true);
    setLogs([]);
    setSummary(null);

    try {
      abortControllerRef.current = new AbortController();

      // Get Firebase auth token
      const user = authFirebase.currentUser;
      let authToken = "";
      if (user) {
        authToken = await user.getIdToken();
      }

      const baseUrl = import.meta.env.VITE_BASEURL || '';
      const response = await fetch(
        `${baseUrl}/scrapadverity/api/adverity/authorization/monitor/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // "X-API-Key": "f2cfd142c6862177cb95ba28a4df4ea7f09e9c3c2768bc83f517d89824284abe",
            "Authorization": `Bearer ${authToken}`,
          },
          body: JSON.stringify(config),
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setIsConnecting(false);
      setIsStreaming(true);

      logAdminAction("Adverity authorization monitoring started", {
        url: `${baseUrl}/scrapadverity/api/adverity/authorization/monitor/stream`,
        method: 'POST',
        status: 200,
        category: 'import',
        action: 'start_onboarding_monitor',
        batchSize: config.batch_size,
        timeoutSeconds: config.timeout_seconds,
      });

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("No reader available");
      }

      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          setIsStreaming(false);
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        let currentEvent = "";
        let currentData = "";

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            currentData = line.slice(6);

            if (currentData && currentEvent) {
              try {
                const parsed = JSON.parse(currentData);
                const logEntry: LogEntry = {
                  id: `${Date.now()}-${Math.random()}`,
                  timestamp: parsed.timestamp || new Date().toISOString(),
                  level: parsed.level || "INFO",
                  message: parsed.message || parsed.raw_message || JSON.stringify(parsed),
                  raw_message: parsed.raw_message,
                  logger_name: parsed.logger_name,
                  event_type: currentEvent as LogEntry["event_type"],
                };

                if (currentEvent === "complete") {
                  setSummary(parsed);
                }

                setLogs((prev) => [...prev, logEntry]);
              } catch (e) {
                console.error("Failed to parse SSE data:", e);
              }
            }

            currentEvent = "";
            currentData = "";
          }
        }
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("Stream aborted by user");
      } else {
        console.error("Streaming error:", error);
        setLogs((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            timestamp: new Date().toISOString(),
            level: "ERROR",
            message: `Connection error: ${error.message}`,
            event_type: "error",
          },
        ]);
      }
    } finally {
      setIsConnecting(false);
      setIsStreaming(false);
    }
  };

  const stopMonitoring = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
  };

  const clearLogs = () => {
    setLogs([]);
    setSummary(null);
  };

  const downloadLogs = () => {
    const logText = logs
      .map((log) => `[${log.timestamp}] [${log.level}] ${log.raw_message || log.message}`)
      .join("\n");
    const blob = new Blob([logText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `onboarding-logs-${new Date().toISOString().split("T")[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetAccountQueue = async () => {
    if (!accountId.trim()) {
      Toastify("error", "Please enter an account ID");
      return;
    }

    setIsResetting(true);

    try {
      const response = await apiClient.post(
        "/scrapadverity/api/adverity/test/queue/reset",
        {
          account_id: accountId.trim(),
          status: "pending",
          last_check_status: "not_found_in_bq",
          minutes_ago: 1,
        }
      );

      const data = response.data;

      if (data.status === "success") {
        Toastify("success", `Account ${accountId} queue status reset successfully`);
        await logAdminAction(`Adverity onboarding queue reset for account ${accountId.trim()}`, {
          url: '/scrapadverity/api/adverity/test/queue/reset',
          method: 'POST',
          status: 200,
          category: 'import',
          action: 'reset_onboarding_queue',
          accountId: accountId.trim(),
        });
        setAccountId("");
      } else {
        Toastify("error", data.detail || data.message || "Failed to reset account queue status");
      }
    } catch (error: any) {
      Toastify("error", error.response?.data?.detail || error.message || "Failed to connect to server");
      await logAdminAction(
        `Failed to reset onboarding queue for account ${accountId.trim()}: ${error.response?.data?.detail || error.message || 'Unknown error'}`,
        { url: '/scrapadverity/api/adverity/test/queue/reset', method: 'POST', category: 'import', action: 'reset_onboarding_queue' },
        true
      );
    } finally {
      setIsResetting(false);
    }
  };

  const formatTime = (timestamp: string): string => {
    try {
      return new Date(timestamp).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch {
      return "--:--:--";
    }
  };

  const getLogLevelClasses = (level: string): string => {
    switch (level.toUpperCase()) {
      case "ERROR":
        return "bg-red-100 text-red-700 border border-red-400";
      case "WARNING":
      case "WARN":
        return "bg-amber-100 text-amber-700 border border-amber-400";
      case "INFO":
        return "bg-green-100 text-green-700 border border-green-400";
      case "DEBUG":
        return "bg-gray-100 text-gray-600 border border-gray-300";
      case "SUCCESS":
        return "bg-green-100 text-green-700 border border-green-400";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const getEventTypeClasses = (eventType: string): string => {
    switch (eventType) {
      case "start":
        return "bg-blue-100 text-blue-700 border border-blue-400";
      case "complete":
        return "bg-green-100 text-green-700 border border-green-400";
      case "error":
        return "bg-red-100 text-red-700 border border-red-400";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  type LogEntryRow = LogEntry & { _srNo: number };
  const logTableData = useMemo<LogEntryRow[]>(
    () => logs.map((log, i) => ({ ...log, _srNo: i + 1 })),
    [logs]
  );

  const logColumns = useMemo((): TableColumn<LogEntryRow>[] => [
    {
      key: "_srNo",
      header: "Sr No",
      width: "10%",
      accessor: (row) => row._srNo,
      render: (_, row) => <span className="text-gray-600">{row._srNo}</span>,
    },
    {
      key: "timestamp",
      header: "Time",
      width: "10%",
      accessor: (row) => row.timestamp,
      render: (_, row) => <span className="text-gray-600">{formatTime(row.timestamp)}</span>,
    },
    {
      key: "level",
      header: "Level",
      width: "10%",
      align: "center" as const,
      accessor: (row) => row.level,
      render: (_, row) => (
        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium uppercase whitespace-nowrap ${getLogLevelClasses(row.level)}`}>
          {row.level}
        </span>
      ),
    },
    {
      key: "event_type",
      header: "Event",
      width: "70%",
      align: "center" as const,
      accessor: (row) => row.event_type,
      render: (_, row) => (
        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap ${getEventTypeClasses(row.event_type)}`}>
          {row.event_type}
        </span>
      ),
    },
    {
      key: "message",
      header: "Message",
      width: "100%",
      accessor: (row) => row.raw_message || row.message,
      render: (_, row) => (
        <span className="text-gray-600 break-words whitespace-pre-wrap">
          {row.raw_message || row.message}
        </span>
      ),
    },
  ], []);

  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader
        title="Onboarding Logs"
        renderCustomTop={
          activeTab === "live-monitoring" ? (
            <div className="flex items-center gap-3 flex-wrap">
              <Input
                placeholder="Account ID"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                disabled={isResetting}
                onKeyDown={(e) => { if (e.key === "Enter") resetAccountQueue(); }}
                className="h-8 w-[150px]"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={resetAccountQueue}
                disabled={isResetting || !accountId.trim()}
                className="h-8 gap-1 text-sm"
              >
                {isResetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                {isResetting ? "Resetting..." : "Reset Queue"}
              </Button>

              <div className="w-px h-6 bg-gray-200" />

              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Batch</span>
                <Input
                  type="number"
                  value={String(config.batch_size)}
                  onChange={(e) => setConfig((prev) => ({ ...prev, batch_size: parseInt(e.target.value) || 10 }))}
                  disabled={isStreaming}
                  className="h-8 w-16 text-center"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Timeout</span>
                <Input
                  type="number"
                  value={String(config.timeout_seconds)}
                  onChange={(e) => setConfig((prev) => ({ ...prev, timeout_seconds: parseInt(e.target.value) || 60 }))}
                  disabled={isStreaming}
                  className="h-8 w-16 text-center"
                />
              </div>

              {hasManagerAccess && (
                !isStreaming ? (
                  <Button
                    size="sm"
                    onClick={startMonitoring}
                    disabled={isConnecting}
                    className="h-8 gap-1.5 bg-[#dbf566] text-[#334238] hover:bg-[#c8e650]"
                  >
                    {isConnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                    {isConnecting ? "Connecting..." : "Start"}
                  </Button>
                ) : (
                  <Button size="sm" variant="destructive" onClick={stopMonitoring} className="h-8 gap-1.5">
                    <Square className="h-3.5 w-3.5" />
                    Stop
                  </Button>
                )
              )}

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" onClick={clearLogs} disabled={isStreaming} className="h-8 w-8">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p>Clear Logs</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" onClick={downloadLogs} disabled={logs.length === 0} className="h-8 w-8">
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p>Download Logs</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <div className="flex items-center gap-1.5 ml-1">
                <Circle className={`h-2 w-2 flex-shrink-0 ${isStreaming ? "text-green-500 fill-green-500 animate-pulse" : "text-gray-400 fill-gray-400"}`} />
                <span className="text-xs text-gray-500">{isStreaming ? "Live" : "Ready"}</span>
                {logs.length > 0 && (
                  <Badge variant="secondary" className="text-xs h-5 bg-[#334238] text-white hover:bg-[#334238]">
                    {logs.length}
                  </Badge>
                )}
              </div>
            </div>
          ) : activeTab === "run-history" ? (
            <div className="flex items-center gap-3 flex-wrap">
              <Select
                value={runTypeFilter}
                onValueChange={(val) => {
                  setRunTypeFilter(val as RunType | "all");
                  setRunLogsPage(0);
                }}
              >
                <SelectTrigger className="w-[200px] h-8">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="authorization_monitor">Authorization Monitor</SelectItem>
                  <SelectItem value="datastream_creation">Datastream Creation</SelectItem>
                  <SelectItem value="backup_fetch">Backup Fetch</SelectItem>
                  <SelectItem value="datastream_update">Datastream Update</SelectItem>
                  <SelectItem value="authorization_sync">Authorization Sync</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={statusFilter}
                onValueChange={(val) => {
                  setStatusFilter(val as RunStatus | "all");
                  setRunLogsPage(0);
                }}
              >
                <SelectTrigger className="w-[140px] h-8">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="running">Running</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchRunLogs()}
                disabled={runLogsLoading}
                className="gap-1 px-3 h-8 text-sm"
              >
                <RefreshCw className={`h-4 w-4 ${runLogsLoading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              {runLogsTotalCount > 0 && (
                <Badge variant="secondary" className="bg-[#334238] text-[#dbf566] hover:bg-[#334238]">
                  {runLogsTotalCount} total
                </Badge>
              )}
            </div>
          ) : activeTab === "event-logs" ? (
            <div className="flex items-center gap-3 flex-wrap">
              <Select
                value={eventTypeFilter}
                onValueChange={(val) => {
                  setEventTypeFilter(val as EventType | "all");
                }}
              >
                <SelectTrigger className="w-[200px] h-8">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="callback_received">Callback Received</SelectItem>
                  <SelectItem value="backup_fetch_triggered">Backup Fetch Triggered</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={eventStatusFilter}
                onValueChange={(val) => {
                  setEventStatusFilter(val as EventStatus | "all");
                }}
              >
                <SelectTrigger className="w-[140px] h-8">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Datastream ID"
                value={datastreamIdFilter}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d+$/.test(v)) setDatastreamIdFilter(v);
                }}
                className="h-8 w-[150px]"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => eventLogsRefreshRef.current?.()}
                disabled={eventLogsLoading}
                className="gap-1 px-3 h-8 text-sm"
              >
                <RefreshCw className={`h-4 w-4 ${eventLogsLoading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              {eventLogsTotalCount > 0 && (
                <Badge variant="secondary" className="bg-[#334238] text-[#dbf566] hover:bg-[#334238]">
                  {eventLogsTotalCount} total
                </Badge>
              )}
            </div>
          ) : undefined
        }
      />

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex flex-col px-8 py-6 bg-white">
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="mb-4">
            <TabsList className="inline-flex w-fit">
              <TabsTrigger value="live-monitoring">Live Monitoring</TabsTrigger>
              <TabsTrigger value="run-history">Run History Logs</TabsTrigger>
              <TabsTrigger value="event-logs">Event Logs</TabsTrigger>
            </TabsList>
          </div>

          {/* Tab: Live Monitoring */}
          <TabsContent value="live-monitoring" className="flex-1 overflow-hidden flex flex-col mt-0">
            {summary && (
              <div className="flex items-center gap-2 px-3 py-1.5 mb-2 rounded-md bg-gray-50 border border-gray-100 flex-shrink-0">
                <span className="text-xs font-medium text-gray-500 mr-1">Summary</span>
                <Badge variant="secondary" className="h-5 text-[11px] bg-white text-gray-700 border border-gray-200">{summary.accounts_checked || 0} checked</Badge>
                <Badge variant="secondary" className="h-5 text-[11px] bg-green-50 text-green-700 border border-green-200">{summary.accounts_authorized || 0} authorized</Badge>
                <Badge variant="secondary" className="h-5 text-[11px] bg-amber-50 text-amber-700 border border-amber-200">{summary.accounts_still_waiting || 0} waiting</Badge>
                <Badge variant="secondary" className="h-5 text-[11px] bg-red-50 text-red-700 border border-red-200">{summary.accounts_errored || 0} errors</Badge>
                <Badge variant="secondary" className="h-5 text-[11px] bg-blue-50 text-blue-700 border border-blue-200">{summary.datastreams_created || 0} streams</Badge>
              </div>
            )}
            <div className="flex-1 min-h-0" ref={logsContainerRef}>
              <DataTable<LogEntryRow>
                columns={logColumns}
                data={logTableData}
                loading={false}
                emptyMessage='Click "Start" to begin streaming logs'
                stickyHeader
                stickyFooter
                showActions={false}
                className="flex-1 min-h-0 overflow-auto"
                maxHeight={summary ? "calc(100vh - 310px)" : "calc(100vh - 280px)"}
              />
            </div>
          </TabsContent>

          {/* Tab: Run History Logs */}
          <TabsContent value="run-history" className="flex-1 overflow-hidden flex flex-col mt-0">
            <div className="flex-1 min-h-0">
              <RunLogsTable
                runLogs={runLogs}
                loading={runLogsLoading}
                totalCount={runLogsTotalCount}
                page={runLogsPage}
                rowsPerPage={runLogsRowsPerPage}
                onPageChange={setRunLogsPage}
                onRowsPerPageChange={(size) => {
                  setRunLogsRowsPerPage(size);
                  setRunLogsPage(0);
                }}
                onShowLogs={handleShowRunLogs}
                onShowErrors={handleShowRunLogs}
              />
            </div>
          </TabsContent>

          {/* Tab: Event Logs */}
          <TabsContent value="event-logs" className="flex-1 overflow-hidden flex flex-col mt-0">
            <div className="flex-1 min-h-0">
              <EventLogsTable
                onViewDetails={handleViewEventDetails}
                eventTypeFilter={eventTypeFilter}
                statusFilter={eventStatusFilter}
                datastreamIdFilter={datastreamIdFilter}
                onLoadingChange={setEventLogsLoading}
                onTotalCountChange={setEventLogsTotalCount}
                refreshRef={eventLogsRefreshRef}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Run Logs Dialog */}
      <RunLogsDialog
        open={showRunLogsDialog}
        onClose={() => setShowRunLogsDialog(false)}
        runId={selectedRunId}
        mode={runLogsDialogMode}
      />

      {/* Event Details Dialog */}
      <EventDetailsDialog
        open={showEventDetailsDialog}
        onClose={() => setShowEventDetailsDialog(false)}
        eventId={selectedEventId}
      />
    </div>
  );
};

export default OnboardingLogs;
